import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/products', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij ophalen producten');
    }
  }
);

export const fetchProduct = createAsyncThunk(
  'products/fetchProduct',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/products/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij ophalen product');
    }
  }
);

export const createProduct = createAsyncThunk(
  'products/createProduct',
  async (productData, { rejectWithValue }) => {
    try {
      const response = await api.post('/products', productData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij aanmaken product');
    }
  }
);

export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/products/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij bijwerken product');
    }
  }
);

export const updateProductMachines = createAsyncThunk(
  'products/updateProductMachines',
  async ({ id, machines }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/products/${id}/machines`, machines);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij bijwerken machine koppeling');
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'products/deleteProduct',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/products/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij verwijderen product');
    }
  }
);

export const fetchProductsByMachine = createAsyncThunk(
  'products/fetchProductsByMachine',
  async ({ machineId, page = 1, limit = 50, search = '' }, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (search) params.search = search;
      const response = await api.get(`/products/by-machine/${machineId}`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij ophalen producten');
    }
  }
);

export const fetchUnassignedProducts = createAsyncThunk(
  'products/fetchUnassignedProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/products/unassigned');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij ophalen producten');
    }
  }
);

export const fetchMaterials = createAsyncThunk(
  'products/fetchMaterials',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/products/meta/materials');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Fout bij ophalen materialen');
    }
  }
);

const initialState = {
  products: [],
  currentProduct: null,
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  },
  loading: false,
  error: null,
  materials: []
};

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.products;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload;
      })
      .addCase(fetchProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.products.unshift(action.payload);
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        const index = state.products.findIndex(p => p._id === action.payload._id);
        if (index !== -1) {
          state.products[index] = action.payload;
        }
        if (state.currentProduct?._id === action.payload._id) {
          state.currentProduct = action.payload;
        }
      })
      .addCase(updateProductMachines.fulfilled, (state, action) => {
        const index = state.products.findIndex(p => p._id === action.payload._id);
        if (index !== -1) {
          state.products[index] = action.payload;
        }
        if (state.currentProduct?._id === action.payload._id) {
          state.currentProduct = action.payload;
        }
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.products = state.products.filter(p => p._id !== action.payload);
      })
      .addCase(fetchMaterials.fulfilled, (state, action) => {
        state.materials = action.payload;
      })
      .addCase(fetchProductsByMachine.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProductsByMachine.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.products || [];
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchProductsByMachine.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearCurrentProduct } = productsSlice.actions;
export default productsSlice.reducer;
